import React from 'react'
import {
  ComponentConfig,
  Slot,
  SlotComponent,
  ComponentData,
  usePuck,
} from '@puckeditor/core'
import { ColorField } from 'breaktime-theme/src/puck/fields/ColorField'
import {
  TypographyProps,
  FontWeight,
  fontFamilyField,
  fontFamilyFallbackField,
  textTransformField,
  fontWeightOptions,
  applyTypography,
} from 'breaktime-theme/src/puck/fields/StyleFields'
import * as styles from './ListicleItem.module.scss'

/**
 * Components that may NOT be dropped inside a list item's content rows.
 * Everything else (generic theme blocks + dedicated listicle content blocks)
 * is allowed, mirroring how PollQuestion guards its header slot.
 */
export const LISTICLE_DISALLOW = [
  'ListicleContainer',
  'ListicleItem',
  'ListicleVoting',
  'PollContainer',
  'PollQuestion',
  'PollAnswer',
  'PollProduct',
  'PollProductPanel',
]

export type ListicleItemProps = {
  id?: string
  subhead?: string
  subheadFontFamily?: TypographyProps['fontFamily']
  subheadFontFamilyFallback?: TypographyProps['fontFamilyFallback']
  subheadTextTransform?: TypographyProps['textTransform']
  subheadFontSize?: string
  subheadFontWeight?: FontWeight
  subheadColor?: string
  /** Layout regions — mirror the rendered .row-top / .row-middle(.col-left|.col-right) / .row-bottom structure */
  topRow: Slot
  middleLeft: Slot
  middleRight: Slot
  bottomRow: Slot
  /** Voting region — drop a ListicleVoting block here to enable voting. */
  voting: Slot
}

export const LISTICLE_ITEM_DEFAULT_PROPS: ListicleItemProps = {
  subhead: 'New List Item',
  topRow: [],
  middleLeft: [],
  middleRight: [],
  bottomRow: [],
  voting: [],
}

type ItemTitleStyleProps = Pick<
  ListicleItemProps,
  | 'subheadFontFamily'
  | 'subheadFontFamilyFallback'
  | 'subheadTextTransform'
  | 'subheadFontSize'
  | 'subheadFontWeight'
  | 'subheadColor'
>

/** Inline style for the item title, from the subhead typography fields. */
export const buildItemTitleStyle = (
  p: ItemTitleStyleProps,
): React.CSSProperties => ({
  ...applyTypography({
    fontFamily: p?.subheadFontFamily,
    fontFamilyFallback: p?.subheadFontFamilyFallback,
    textTransform: p?.subheadTextTransform,
  }),
  ...(p?.subheadFontSize ? { fontSize: p.subheadFontSize } : {}),
  ...(p?.subheadFontWeight
    ? { fontWeight: Number(p.subheadFontWeight) || undefined }
    : {}),
  ...(p?.subheadColor ? { color: p.subheadColor } : {}),
})

export const PuckListicleItem: ComponentConfig<ListicleItemProps> = {
  label: 'Listicle List Item',
  defaultProps: LISTICLE_ITEM_DEFAULT_PROPS,
  fields: {
    subhead: { type: 'text', label: 'Item Title' },
    subheadFontFamily: fontFamilyField,
    subheadFontFamilyFallback: fontFamilyFallbackField,
    subheadTextTransform: textTransformField,
    subheadFontSize: { type: 'text', label: 'Title Font Size', placeholder: '24px' },
    subheadFontWeight: { type: 'select', label: 'Title Font Weight', options: fontWeightOptions },
    subheadColor: {
      type: 'custom',
      label: 'Title Color',
      render: (props: React.ComponentProps<typeof ColorField>) => (
        <ColorField {...props} clearable />
      ),
    },
    topRow: { type: 'slot', label: 'Top Row', disallow: LISTICLE_DISALLOW },
    middleLeft: {
      type: 'slot',
      label: 'Middle Row — Left',
      disallow: LISTICLE_DISALLOW,
    },
    middleRight: {
      type: 'slot',
      label: 'Middle Row — Right',
      disallow: LISTICLE_DISALLOW,
    },
    bottomRow: {
      type: 'slot',
      label: 'Bottom Row',
      disallow: LISTICLE_DISALLOW,
    },
    voting: { type: 'slot', label: 'Voting', allow: ['ListicleVoting'] },
  },
  // This render only runs in the editor. In production ListicleContainer reads
  // these props, renders the row slots via <Render /> and feeds the legacy
  // ListicleStep runtime (see ListicleContainer.tsx).
  render: props => <ListicleItemRender {...props} />,
}

type ListicleItemRenderProps = Omit<
  ListicleItemProps,
  'topRow' | 'middleLeft' | 'middleRight' | 'bottomRow' | 'voting'
> & {
  topRow: SlotComponent
  middleLeft: SlotComponent
  middleRight: SlotComponent
  bottomRow: SlotComponent
  voting: SlotComponent
}

// Container props we read off the sibling tree to mirror production numbering.
type ContainerNumberingProps = {
  items?: ComponentData[]
  showNumbers?: boolean
  displayFormat?: 'slideshow' | 'list'
}

function ListicleItemRender(props: ListicleItemRenderProps) {
  const {
    id,
    subhead,
    topRow: TopRow,
    middleLeft: MiddleLeft,
    middleRight: MiddleRight,
    bottomRow: BottomRow,
    voting: Voting,
  } = props
  const titleStyle = buildItemTitleStyle(props)
  // Mirror production numbering (ListiclePanel): the container's
  // "Show Item Numbers" field + this item's 1-based position among siblings.
  const puck = usePuck()
  const content = (puck?.appState?.data?.content ?? []) as ComponentData[]
  const container = content.find(c => c.type === 'ListicleContainer')
  const containerProps = (container?.props ?? {}) as ContainerNumberingProps
  const items = containerProps.items ?? []
  const showNumbers = containerProps.showNumbers ?? true
  const displayFormat = containerProps.displayFormat ?? 'slideshow'
  const total = items.length || 1
  const index = items.findIndex(it => it.props?.id === id)
  const position = index >= 0 ? index : 0
  const numberPrefix = showNumbers && index >= 0 ? `${index + 1}. ` : ''

  return (
    <div className={`db-react-slide ${styles.listicleItem}`}>
      {/* Slideshow progress preview — mirrors ListiclePanel's per-slide header.
          Arrows are non-functional in the editor (each item shows stacked). */}
      {displayFormat === 'slideshow' && (
        <div className="quiz__progress">
          <span className="progress-label">List</span>
          <span className="quiz__progress-number">
            {position + 1} of {total}
          </span>
          <ul className="quiz__progress-dots">
            {Array.from({ length: total }).map((_, k) => (
              <li key={k} className={k === position ? 'active' : ''}></li>
            ))}
          </ul>
          <ul className="inner-arrows">
            <li>
              <button type="button" className="inner-prev" aria-label="Previous"></button>
            </li>
            <li>
              <button type="button" className="inner-next" aria-label="Next"></button>
            </li>
          </ul>
        </div>
      )}
      {subhead && (
        <h2 className="step-content__title" style={titleStyle}>
          {numberPrefix}
          {subhead}
        </h2>
      )}
      <div className="listicle-item-contents">
        <div className="row row-top">
          <span className={styles.rowLabel}>Top Row</span>
          <TopRow
            disallow={LISTICLE_DISALLOW}
            className={styles.rowSlot}
            minEmptyHeight={24}
          />
        </div>
        <div className="row row-middle">
          <div className="col col-left">
            <span className={styles.rowLabel}>Middle — Left</span>
            <MiddleLeft
              disallow={LISTICLE_DISALLOW}
              className={styles.rowSlot}
              minEmptyHeight={24}
            />
          </div>
          <div className="col col-right">
            <span className={styles.rowLabel}>Middle — Right</span>
            <MiddleRight
              disallow={LISTICLE_DISALLOW}
              className={styles.rowSlot}
              minEmptyHeight={24}
            />
          </div>
        </div>
        <div className="row row-bottom">
          <span className={styles.rowLabel}>Bottom Row</span>
          <BottomRow
            disallow={LISTICLE_DISALLOW}
            className={styles.rowSlot}
            minEmptyHeight={24}
          />
        </div>
        <div className="row row-voting">
          <span className={styles.rowLabel}>Voting (optional)</span>
          <Voting
            allow={['ListicleVoting']}
            className={styles.rowSlot}
            minEmptyHeight={24}
          />
        </div>
      </div>
    </div>
  )
}
